import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withKeys as _withKeys, withModifiers as _withModifiers, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "gs-box",
  role: "dialog",
  "aria-label": "Busca global"
}
const _hoisted_2 = { class: "gs-input-row" }
const _hoisted_3 = ["onKeydown"]
const _hoisted_4 = {
  key: 1,
  class: "gs-shortcut"
}
const _hoisted_5 = { class: "gs-body" }
const _hoisted_6 = {
  key: 0,
  class: "gs-state"
}
const _hoisted_7 = {
  key: 1,
  class: "gs-state gs-state--hint"
}
const _hoisted_8 = {
  key: 2,
  class: "gs-state"
}
const _hoisted_9 = {
  key: 3,
  class: "gs-results"
}
const _hoisted_10 = ["onClick", "onMouseenter"]
const _hoisted_11 = { class: "gs-avatar" }
const _hoisted_12 = { class: "gs-item-body" }
const _hoisted_13 = { class: "gs-item-title" }
const _hoisted_14 = { class: "gs-item-sub" }
const _hoisted_15 = {
  key: 0,
  class: "gs-item-extra"
}
const _hoisted_16 = {
  key: 0,
  class: "gs-item-extra-protocol",
  title: "Protocolo"
}
const _hoisted_17 = { key: 1 }
const _hoisted_18 = {
  key: 2,
  class: "gs-item-extra-fluxo"
}
const _hoisted_19 = ["onClick", "onMouseenter"]
const _hoisted_20 = { class: "gs-avatar gs-avatar--contact" }
const _hoisted_21 = { class: "gs-item-body" }
const _hoisted_22 = { class: "gs-item-title" }
const _hoisted_23 = { class: "gs-item-sub" }

import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { globalSearchOpen, closeGlobalSearch } from '@/composables/useGlobalSearch'
import { requestOpenChat } from '@/composables/useChatTrigger'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { fmtPhone } from '@/utils/contactsFormat'
import { hiddenFluxoIdsCp, isFluxoHiddenCp } from '@/composables/useHiddenFluxosCp'
import { listConversations, STATUS_LABEL, type ConversationListItem } from '@/services/wootdashConversations'
import { searchContacts, type CpContact } from '@/services/wootdashContacts'

// Busca global (Ctrl K) no WootDash: conversas e contatos pelo texto (nome, telefone…).
// A conversa abre no painel lateral (ConvDrawer) — o popup antigo dependia do n8n.

export default /*@__PURE__*/_defineComponent({
  __name: 'GlobalSearch',
  setup(__props) {

const open  = globalSearchOpen
const store  = useStore()
const router = useRouter()
const { mode: railMode } = useChatRailMode()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')

const query        = ref('')
const loading      = ref(false)
const focusedIndex = ref(0)
const inputRef     = ref<HTMLInputElement | null>(null)
const results      = ref<{ conversations: ConversationListItem[], contacts: CpContact[] }>({ conversations: [], contacts: [] })

const hasResults  = computed(() => results.value.conversations.length > 0 || results.value.contacts.length > 0)
const totalItems  = computed(() => results.value.conversations.length + results.value.contacts.length)

// Classe do selo (as cores já existem pra open/pending/resolved).
function statusKey(status: number): string {
  return status === 0 ? 'open' : status === 1 ? 'pending' : 'resolved'
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(open, (val) => {
  if (val) {
    query.value = ''
    results.value = { conversations: [], contacts: [] }
    focusedIndex.value = 0
    nextTick(() => inputRef.value?.focus())
  }
})

watch(query, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  focusedIndex.value = 0
  if (!val || val.length < 2) {
    results.value = { conversations: [], contacts: [] }
    loading.value = false
    return
  }
  loading.value = true
  debounceTimer = setTimeout(() => doSearch(val), 300)
})

let searchSeq = 0

async function doSearch(q: string) {
  if (!token.value) { loading.value = false; return }
  const seq = ++searchSeq
  try {
    const [convs, contacts] = await Promise.all([
      listConversations(token.value, { search: q.trim(), pageSize: 8, excludeFlowIds: hiddenFluxoIdsCp.value }),
      searchContacts(token.value, q.trim(), 1, 5),
    ])
    if (seq !== searchSeq) return // resposta de uma busca já obsoleta (query mudou nesse meio-tempo)
    // Fluxo escondido (robô/IA) não aparece pro agente em nenhuma tela — nem aqui.
    results.value = {
      conversations: convs.conversations.filter(c => !isFluxoHiddenCp(c.flowId)).slice(0, 5),
      contacts: contacts.contacts,
    }
  } catch {
    if (seq !== searchSeq) return
    results.value = { conversations: [], contacts: [] }
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

function close() { closeGlobalSearch() }

function openConv(conv: ConversationListItem) {
  railMode.value = 'client' // o painel de conversa fica escondido se o trilho estiver no chat interno
  requestOpenChat(conv.id)
  close()
}

function openContact(contact: CpContact) {
  router.push({ path: '/contacts', query: { q: contact.name } })
  close()
}

function moveFocus(dir: number) {
  if (totalItems.value === 0) return
  focusedIndex.value = (focusedIndex.value + dir + totalItems.value) % totalItems.value
}

function selectFocused() {
  if (!hasResults.value) return
  const convCount = results.value.conversations.length
  if (focusedIndex.value < convCount) {
    openConv(results.value.conversations[focusedIndex.value])
  } else {
    openContact(results.value.contacts[focusedIndex.value - convCount])
  }
}

function initials(name?: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function convDisplayName(conv: ConversationListItem): string {
  return conv.contactName?.trim() || conv.contactPhone?.trim() || 'Conversa'
}

function convPhone(conv: ConversationListItem): string | null {
  const phone = conv.contactPhone?.trim()
  return phone ? fmtPhone(phone) : null
}

function convInitials(conv: ConversationListItem): string {
  const name = conv.contactName?.trim()
  if (name) return initials(name)
  const phone = conv.contactPhone?.trim()
  return phone ? phone.slice(-2) : '#'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "gs-fade" }, {
      default: _withCtx(() => [
        (_unref(open))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "gs-overlay",
              onMousedown: _withModifiers(close, ["self"])
            }, [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _cache[5] || (_cache[5] = _createElementVNode("svg", {
                    class: "gs-search-icon",
                    width: "16",
                    height: "16",
                    viewBox: "0 0 24 24",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2.5",
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round"
                  }, [
                    _createElementVNode("circle", {
                      cx: "11",
                      cy: "11",
                      r: "8"
                    }),
                    _createElementVNode("line", {
                      x1: "21",
                      y1: "21",
                      x2: "16.65",
                      y2: "16.65"
                    })
                  ], -1)),
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "inputRef",
                    ref: inputRef,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
                    class: "gs-input",
                    placeholder: "Buscar conversas, contatos, protocolo...",
                    autocomplete: "off",
                    spellcheck: "false",
                    onKeydown: [
                      _withKeys(close, ["esc"]),
                      _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (moveFocus(1)), ["prevent"]), ["down"])),
                      _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (moveFocus(-1)), ["prevent"]), ["up"])),
                      _withKeys(_withModifiers(selectFocused, ["prevent"]), ["enter"])
                    ]
                  }, null, 40, _hoisted_3), [
                    [_vModelText, query.value]
                  ]),
                  (query.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "gs-clear",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => {query.value = ''; inputRef.value?.focus()})
                      }, [...(_cache[4] || (_cache[4] = [
                        _createElementVNode("svg", {
                          width: "14",
                          height: "14",
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2.5",
                          "stroke-linecap": "round"
                        }, [
                          _createElementVNode("line", {
                            x1: "18",
                            y1: "6",
                            x2: "6",
                            y2: "18"
                          }),
                          _createElementVNode("line", {
                            x1: "6",
                            y1: "6",
                            x2: "18",
                            y2: "18"
                          })
                        ], -1)
                      ]))]))
                    : (_openBlock(), _createElementBlock("kbd", _hoisted_4, "Ctrl K"))
                ]),
                _createElementVNode("div", _hoisted_5, [
                  (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("span", { class: "gs-spinner" }, null, -1),
                        _createTextVNode(" Buscando... ", -1)
                      ]))]))
                    : (!query.value || query.value.length < 2)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[7] || (_cache[7] = [
                          _createElementVNode("svg", {
                            width: "32",
                            height: "32",
                            viewBox: "0 0 24 24",
                            fill: "none",
                            stroke: "currentColor",
                            "stroke-width": "1.5",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          }, [
                            _createElementVNode("circle", {
                              cx: "11",
                              cy: "11",
                              r: "8"
                            }),
                            _createElementVNode("line", {
                              x1: "21",
                              y1: "21",
                              x2: "16.65",
                              y2: "16.65"
                            })
                          ], -1),
                          _createElementVNode("span", null, "Digite nome, telefone ou protocolo pra buscar conversas e contatos", -1)
                        ]))]))
                      : (!hasResults.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _cache[8] || (_cache[8] = _createTextVNode(" Nenhum resultado para \"", -1)),
                            _createElementVNode("strong", null, _toDisplayString(query.value), 1),
                            _cache[9] || (_cache[9] = _createTextVNode("\" ", -1))
                          ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            (results.value.conversations.length)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  _cache[11] || (_cache[11] = _createElementVNode("div", { class: "gs-group-label" }, [
                                    _createElementVNode("svg", {
                                      width: "11",
                                      height: "11",
                                      viewBox: "0 0 24 24",
                                      fill: "none",
                                      stroke: "currentColor",
                                      "stroke-width": "2.5",
                                      "stroke-linecap": "round",
                                      "stroke-linejoin": "round"
                                    }, [
                                      _createElementVNode("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" })
                                    ]),
                                    _createTextVNode(" Conversas ")
                                  ], -1)),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(results.value.conversations, (conv, i) => {
                                    return (_openBlock(), _createElementBlock("button", {
                                      key: 'cv' + conv.id,
                                      class: _normalizeClass(["gs-item", { 'gs-item--focus': focusedIndex.value === i }]),
                                      onClick: ($event: any) => (openConv(conv)),
                                      onMouseenter: ($event: any) => (focusedIndex.value = i)
                                    }, [
                                      _createElementVNode("span", _hoisted_11, _toDisplayString(convInitials(conv)), 1),
                                      _createElementVNode("span", _hoisted_12, [
                                        _createElementVNode("span", _hoisted_13, _toDisplayString(convDisplayName(conv)), 1),
                                        _createElementVNode("span", _hoisted_14, [
                                          (conv.teamName)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _createTextVNode(_toDisplayString(conv.teamName), 1)
                                              ], 64))
                                            : _createCommentVNode("", true),
                                          _createElementVNode("span", {
                                            class: _normalizeClass(["gs-badge", 'gs-badge--' + statusKey(conv.status)])
                                          }, _toDisplayString(_unref(STATUS_LABEL)[conv.status] ?? '—'), 3)
                                        ]),
                                        (convPhone(conv) || conv.flowName || conv.protocol)
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
                                              (conv.protocol)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_16, "#" + _toDisplayString(conv.protocol), 1))
                                                : _createCommentVNode("", true),
                                              (convPhone(conv))
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(convPhone(conv)), 1))
                                                : _createCommentVNode("", true),
                                              (conv.flowName)
                                                ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(conv.flowName), 1))
                                                : _createCommentVNode("", true)
                                            ]))
                                          : _createCommentVNode("", true)
                                      ]),
                                      _cache[10] || (_cache[10] = _createElementVNode("svg", {
                                        class: "gs-item-arrow",
                                        width: "14",
                                        height: "14",
                                        viewBox: "0 0 24 24",
                                        fill: "none",
                                        stroke: "currentColor",
                                        "stroke-width": "2",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      }, [
                                        _createElementVNode("polyline", { points: "9 18 15 12 9 6" })
                                      ], -1))
                                    ], 42, _hoisted_10))
                                  }), 128))
                                ], 64))
                              : _createCommentVNode("", true),
                            (results.value.contacts.length)
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createElementVNode("div", {
                                    class: "gs-group-label",
                                    style: _normalizeStyle(results.value.conversations.length ? 'margin-top:10px' : '')
                                  }, [...(_cache[12] || (_cache[12] = [
                                    _createElementVNode("svg", {
                                      width: "11",
                                      height: "11",
                                      viewBox: "0 0 24 24",
                                      fill: "none",
                                      stroke: "currentColor",
                                      "stroke-width": "2.5",
                                      "stroke-linecap": "round",
                                      "stroke-linejoin": "round"
                                    }, [
                                      _createElementVNode("path", { d: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" }),
                                      _createElementVNode("circle", {
                                        cx: "12",
                                        cy: "7",
                                        r: "4"
                                      })
                                    ], -1),
                                    _createTextVNode(" Contatos ", -1)
                                  ]))], 4),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(results.value.contacts, (contact, i) => {
                                    return (_openBlock(), _createElementBlock("button", {
                                      key: 'ct' + contact.id,
                                      class: _normalizeClass(["gs-item", { 'gs-item--focus': focusedIndex.value === results.value.conversations.length + i }]),
                                      onClick: ($event: any) => (openContact(contact)),
                                      onMouseenter: ($event: any) => (focusedIndex.value = results.value.conversations.length + i)
                                    }, [
                                      _createElementVNode("span", _hoisted_20, _toDisplayString(initials(contact.name)), 1),
                                      _createElementVNode("span", _hoisted_21, [
                                        _createElementVNode("span", _hoisted_22, _toDisplayString(contact.name), 1),
                                        _createElementVNode("span", _hoisted_23, _toDisplayString(contact.phone ? _unref(fmtPhone)(contact.phone) : (contact.email || '—')), 1)
                                      ]),
                                      _cache[13] || (_cache[13] = _createElementVNode("svg", {
                                        class: "gs-item-arrow",
                                        width: "14",
                                        height: "14",
                                        viewBox: "0 0 24 24",
                                        fill: "none",
                                        stroke: "currentColor",
                                        "stroke-width": "2",
                                        "stroke-linecap": "round",
                                        "stroke-linejoin": "round"
                                      }, [
                                        _createElementVNode("polyline", { points: "9 18 15 12 9 6" })
                                      ], -1))
                                    ], 42, _hoisted_19))
                                  }), 128))
                                ], 64))
                              : _createCommentVNode("", true)
                          ]))
                ]),
                _cache[14] || (_cache[14] = _createElementVNode("div", { class: "gs-footer" }, [
                  _createElementVNode("span", null, [
                    _createElementVNode("kbd", null, "↑↓"),
                    _createTextVNode(" navegar")
                  ]),
                  _createElementVNode("span", null, [
                    _createElementVNode("kbd", null, "↵"),
                    _createTextVNode(" abrir")
                  ]),
                  _createElementVNode("span", null, [
                    _createElementVNode("kbd", null, "Esc"),
                    _createTextVNode(" fechar")
                  ])
                ], -1))
              ])
            ], 32))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})